import { DataGrid, NoRecords, StatusBadge } from '@ijoin/components';
import {
  AdminExportQueueSnapshotAllocationRes,
  AdminExportQueueSnapshotRes,
  AdminRecordKeeperExportQueueDetailsRes,
  AdminRecordKeeperExportQueueDiffEntryRes
} from '@ijoin/hooks';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { GridColDef } from '@mui/x-data-grid';

import { FC, useCallback } from 'react';

const ALLOCATION_PREFIX = 'allocation ';
const DEMOGRAPHIC_PREFIX = 'demographic ';

type DemographicKey = keyof NonNullable<
  AdminExportQueueSnapshotRes['demographics']
>;

const DEMOGRAPHIC_FIELDS: { key: DemographicKey; label: string }[] = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'dateOfBirth', label: 'Date of Birth' },
  { key: 'gender', label: 'Gender' },
  { key: 'addressOne', label: 'Address 1' },
  { key: 'addressTwo', label: 'Address 2' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zipCode', label: 'Zip Code' },
  { key: 'cellPhone', label: 'Cell Phone' },
  { key: 'workPhone', label: 'Work Phone' },
  { key: 'personalEmail', label: 'Personal Email' },
  { key: 'workEmail', label: 'Work Email' }
];

type ChangeKind = 'added' | 'changed' | 'removed';

/* allocation before/after are percents, the absent side comes through as '0' */
const changeKind = (
  entry: AdminRecordKeeperExportQueueDiffEntryRes
): ChangeKind => {
  const isAllocation = entry.field.startsWith(ALLOCATION_PREFIX);
  const isEmptyBefore = isAllocation ? entry.before === '0' : !entry.before;
  const isEmptyAfter = isAllocation ? entry.after === '0' : !entry.after;

  if (isEmptyBefore && !isEmptyAfter) return 'added';
  if (!isEmptyBefore && isEmptyAfter) return 'removed';

  return 'changed';
};

const CHANGE_BADGES = {
  added: { label: 'Added', severity: 'success' },
  changed: { label: 'Changed', severity: 'warning' },
  removed: { label: 'Removed', severity: 'error' }
} as const;

type SuppressionFlag = keyof Pick<
  AdminRecordKeeperExportQueueDetailsRes,
  | 'isPreTaxContributionSuppressed'
  | 'isPreTaxIncreaseSuppressed'
  | 'isRothContributionSuppressed'
  | 'isRothIncreaseSuppressed'
>;

/* a source the export withheld is omitted, as the advisor submission log omits it */
const SCALAR_FIELDS: {
  key: Exclude<
    keyof AdminExportQueueSnapshotRes,
    'allocations' | 'demographics'
  >;
  label: string;
  suppressedBy?: SuppressionFlag;
}[] = [
  {
    key: 'preTaxContributionType',
    label: 'Pre-Tax Type',
    suppressedBy: 'isPreTaxContributionSuppressed'
  },
  {
    key: 'preTaxContributionPercent',
    label: 'Pre-Tax %',
    suppressedBy: 'isPreTaxContributionSuppressed'
  },
  {
    key: 'preTaxContributionDollars',
    label: 'Pre-Tax $',
    suppressedBy: 'isPreTaxContributionSuppressed'
  },
  {
    key: 'postTaxContributionType',
    label: 'Post-Tax Type',
    suppressedBy: 'isRothContributionSuppressed'
  },
  {
    key: 'postTaxContributionPercent',
    label: 'Post-Tax %',
    suppressedBy: 'isRothContributionSuppressed'
  },
  {
    key: 'postTaxContributionDollars',
    label: 'Post-Tax $',
    suppressedBy: 'isRothContributionSuppressed'
  },
  {
    key: 'autoIncreasePreTaxAnnualPercent',
    label: 'Auto Increase Pre-Tax Annual %',
    suppressedBy: 'isPreTaxIncreaseSuppressed'
  },
  {
    key: 'autoIncreasePreTaxMaximumPercent',
    label: 'Auto Increase Pre-Tax Max %',
    suppressedBy: 'isPreTaxIncreaseSuppressed'
  },
  {
    key: 'autoIncreaseRothAnnualPercent',
    label: 'Auto Increase Roth Annual %',
    suppressedBy: 'isRothIncreaseSuppressed'
  },
  {
    key: 'autoIncreaseRothMaximumPercent',
    label: 'Auto Increase Roth Max %',
    suppressedBy: 'isRothIncreaseSuppressed'
  },
  { key: 'totalContributionTypeId', label: 'Total Contribution Type' }
];

/* mirrors the server's diff key: ticker upper-cased, else externalId */
const allocationKey = (allocation: AdminExportQueueSnapshotAllocationRes) => {
  if (allocation.tickerSymbol) {
    return `ticker:${allocation.tickerSymbol.toUpperCase()}`;
  }

  return allocation.externalId ? `externalId:${allocation.externalId}` : null;
};

const allocationLabel = (allocation: AdminExportQueueSnapshotAllocationRes) =>
  allocation.tickerSymbol ??
  allocation.externalId ??
  allocation.cusip ??
  allocation.fundName ??
  '';

type ComparisonRow = {
  changeKind: ChangeKind;
  id: string;
  initial?: number | string;
  label: string;
  submitted?: number | string;
  type: 'Allocation' | 'Contribution' | 'Demographic';
};

type ComparisonDetails = Partial<
  Pick<AdminRecordKeeperExportQueueDetailsRes, SuppressionFlag>
> &
  Pick<
    AdminRecordKeeperExportQueueDetailsRes,
    'diff' | 'initialSnapshot' | 'submittedSnapshot'
  >;

type ComparisonRowDraft = Omit<ComparisonRow, 'changeKind'> & {
  changeKind?: ChangeKind;
};

const buildComparisonRows = (details?: ComparisonDetails): ComparisonRow[] => {
  const initialAllocations = details?.initialSnapshot?.allocations ?? [];
  const submittedAllocations = details?.submittedSnapshot?.allocations ?? [];
  const diffByField = new Map(
    (details?.diff ?? []).map(entry => [entry.field, entry])
  );

  const submittedByKey = new Map(
    submittedAllocations.map(allocation => [
      allocationKey(allocation),
      allocation
    ])
  );
  const initialKeys = new Set(initialAllocations.map(allocationKey));

  const allocationRows: ComparisonRowDraft[] = [
    ...initialAllocations.map((allocation, i) => {
      const key = allocationKey(allocation);
      const entry = key
        ? diffByField.get(`${ALLOCATION_PREFIX}${key}`)
        : undefined;

      return {
        changeKind: entry && changeKind(entry),
        id: `allocation-${key ?? `initial-${i}`}`,
        initial: allocation.percentAllocation,
        label: allocationLabel(allocation),
        submitted: key ? submittedByKey.get(key)?.percentAllocation : undefined,
        type: 'Allocation' as const
      };
    }),
    ...submittedAllocations
      .filter(allocation => {
        const key = allocationKey(allocation);

        return !(key && initialKeys.has(key));
      })
      .map((allocation, i) => {
        const key = allocationKey(allocation);
        const entry = key
          ? diffByField.get(`${ALLOCATION_PREFIX}${key}`)
          : undefined;

        return {
          changeKind: entry && changeKind(entry),
          id: `allocation-${key ?? `submitted-${i}`}`,
          initial: undefined,
          label: allocationLabel(allocation),
          submitted: allocation.percentAllocation,
          type: 'Allocation' as const
        };
      })
  ];

  const scalarRows: ComparisonRowDraft[] = SCALAR_FIELDS.filter(
    field =>
      !(field.suppressedBy && details?.[field.suppressedBy]) &&
      (details?.initialSnapshot?.[field.key] != null ||
        details?.submittedSnapshot?.[field.key] != null)
  ).map(field => {
    const entry = diffByField.get(field.key);

    return {
      changeKind: entry && changeKind(entry),
      id: field.key,
      initial: details?.initialSnapshot?.[field.key],
      label: field.label,
      submitted: details?.submittedSnapshot?.[field.key],
      type: 'Contribution' as const
    };
  });

  const demographicRows: ComparisonRowDraft[] = DEMOGRAPHIC_FIELDS.map(
    field => {
      const entry = diffByField.get(`${DEMOGRAPHIC_PREFIX}${field.key}`);

      return {
        changeKind: entry && changeKind(entry),
        id: `demographic-${field.key}`,
        initial: details?.initialSnapshot?.demographics?.[field.key],
        label: field.label,
        submitted: details?.submittedSnapshot?.demographics?.[field.key],
        type: 'Demographic' as const
      };
    }
  );

  return [...allocationRows, ...scalarRows, ...demographicRows].filter(
    (row): row is ComparisonRow => Boolean(row.changeKind)
  );
};

const buildComparisonColumns = (
  submittedHeaderName: string
): GridColDef<ComparisonRow>[] => [
  { field: 'type', flex: 1, headerName: 'Type', sortable: false },
  { field: 'label', flex: 1.25, headerName: 'Field', sortable: false },
  {
    cellClassName: params =>
      params.row.changeKind === 'added' ? '' : 'struckCell',
    field: 'initial',
    flex: 1,
    headerName: 'Initial (last RK sync)',
    renderCell: params =>
      params.row.changeKind === 'added' ? '' : (params.row.initial ?? ''),
    sortable: false
  },
  {
    align: 'center',
    cellClassName: 'directionCell',
    field: 'direction',
    headerName: '',
    renderCell: params =>
      params.row.changeKind === 'changed' ? <ArrowForwardIcon /> : null,
    sortable: false,
    width: 56
  },
  {
    field: 'submitted',
    flex: 1,
    headerName: submittedHeaderName,
    renderCell: params =>
      params.row.changeKind === 'removed' ? '' : (params.row.submitted ?? ''),
    sortable: false
  },
  {
    field: 'changeKind',
    flex: 1,
    headerName: 'Change',
    renderCell: params => (
      <StatusBadge severity={CHANGE_BADGES[params.row.changeKind].severity}>
        {CHANGE_BADGES[params.row.changeKind].label}
      </StatusBadge>
    ),
    sortable: false
  }
];

export const SubmissionDiffSections: FC<{
  details: ComparisonDetails;
  submittedHeaderName?: string;
}> = props => {
  const comparisonRows = buildComparisonRows(props.details);
  const comparisonColumns = buildComparisonColumns(
    props.submittedHeaderName ?? 'Submitted'
  );
  const hasSnapshot = Boolean(
    props.details.initialSnapshot || props.details.submittedSnapshot
  );

  const noRowsOverlay = useCallback(
    () => (
      <NoRecords message={hasSnapshot ? 'No changes' : 'No snapshot data'} />
    ),
    [hasSnapshot]
  );

  return (
    <DataGrid
      columns={comparisonColumns}
      disableColumnMenu
      disableRowSelectionOnClick
      getRowId={row => row.id}
      hideFooter
      rows={comparisonRows}
      showColumnMenu
      slotProps={{
        root: {
          'data-component': 'dataGrid',
          'data-testid': 'snapshotComparison'
        }
      }}
      slots={{ noRowsOverlay }}
      sx={{
        '& .directionCell .MuiSvgIcon-root': {
          verticalAlign: 'middle'
        },
        '& .struckCell': {
          color: 'text.secondary',
          textDecoration: 'line-through'
        }
      }}
    />
  );
};

SubmissionDiffSections.displayName = 'SubmissionDiffSections';
